<template>
  <div :id="chartId" class="container-chart"></div>
</template>

<script>
import * as d3 from 'd3'
import d3Tip from 'd3-tip'
import moment from 'moment'

export default {
  name: 'RotationGradientChart',
  data() {
    return {
      margin: { top: 20, right: 20, bottom: 30, left: 50 },
      width: 960,
      height: 450,
      radius: 200,
      svgWidth: 960,
      svgHeight: 500,
      svg: Object(),
      tooltip: Object(),
      chart: Object(),
      xAxis: Object(),
      yAxis: Object(),
      xAxisScale: null,
      yAxisScale: null,
      loading: true,
      gradients: [],
      colors: ['#A8FE85', '#EDF580', '#FEB37C', '#FD8479', '#FF6F6B'],
      fields: {
        level_points: {
          gradient: ['0 : -0.2', '-0.2 : -0.4', '-0.4 : -0.6', '-0.6 : -0.8', '> -0.8'],
          max: ['0 : 20', '20 : 40', '40 : 60', '60 : 80', '> 80'],
          min: ['0 : 20', '20 : 40', '40 : 60', '60 : 80', '> 80'],
        },
        temperature_points: {
          gradient: ['0 : 0.2', '0.2 : 0.4', '0.4 : 0.6', '0.6 : 0.8', '> 0.8'],
          max: ['< 30', '30 : 40', '40 : 50', '50 : 60', '> 60'],
          min: ['< 30', '30 : 40', '40 : 50', '50 : 60', '> 60'],
          average: ['< 30', '30 : 40', '40 : 50', '50 : 60', '> 60'],
        },
      },
    }
  },
  props: ['dataset', 'chartId', 'site', 'batteryForm'],
  mounted() {
    d3.tip = d3Tip
    this.initSvgContainer()
    this.calculatePath()
  },
  watch: {
    dataset: {
      handler: function() {
        this.calculatePath()
      },
      immediate: true,
    },
    batteryForm: {
      handler: function() {},
      immediate: true,
    },
  },
  methods: {
    initSvgContainer: function() {
      this.svgWidth = this.width + this.margin.left + this.margin.right
      this.svgHeight = this.height + this.margin.top * 2 + this.margin.bottom * 2

      this.gridSize = Math.floor(this.svgWidth / 72)

      this.svg = d3
        .select('#' + this.chartId)
        .append('svg')
        .attr('width', '100%')
        .attr('viewBox', '0 0 ' + this.svgWidth + ' ' + this.svgHeight)
        .attr('preserveAspectRatio', 'xMaxYMin')
        .append('g')
        .attr('transform', 'translate(' + this.margin.left + ',' + this.margin.top + ')')

      this.tooltip = d3
        .tip()
        .attr('class', 'd3-tip')
        .html(function(d) {
          return d.device_serial + ': -' + d.percent
        })

      this.tooltip.hide

      this.svg.call(this.tooltip)
    },
    calculatePath: function() {
      let that = this

      if (this.gradientsUrl == '') {
        return
      }

      this.svgWidth = this.width + this.margin.left + this.margin.right
      this.svgHeight = this.height + this.margin.top * 2 + this.margin.bottom * 2

      d3.select('#' + that.chartId + ' svg').remove()

      that.svg = d3
        .select('#' + that.chartId)
        .append('svg')
        .attr('width', '100%')
        .attr('viewBox', '0 0 ' + that.svgWidth + ' ' + that.svgHeight)
        .attr('preserveAspectRatio', 'xMaxYMin')
        .append('g')
        .attr('transform', 'translate(' + that.margin.left + ',' + that.margin.top * 2 + ')')

      // group the data: one array for each value of the X .
      this.dataset.forEach(item => {
        item.date = moment(item.date, 'YYYY-MM-DD')
      })

      var sumstat = d3
        .nest()
        .key(function(d) {
          return d.date
        })
        .entries(this.dataset)

      // Stack the data: each group will be represented on top of each other
      var mygroups = that.fields[that.batteryForm.data_value][that.batteryForm.data_type]
      var mygroup = [] // list of group names
      mygroups.forEach((g, index) => {
        mygroup.push(index + 1)
      })
      var stackedData = d3
        .stack()
        .keys(mygroup)
        .value(function(d, key) {
          return d.values[key - 1].count
        })(sumstat)

      let axisDateFormat = that.$locale == 'en' ? '%m/%d/%Y' : '%d/%m/%Y'

      // Add X axis --> it is a date format
      var x = d3
        .scaleTime()
        .domain(
          d3.extent(this.dataset, function(d) {
            return d.date
          }),
        )
        .range([0, that.width - 100])

      const months = Math.round(
        moment
          .duration(this.dataset[this.dataset.length - 2].date.diff(this.dataset[0].date))
          .asMonths(),
      )

      let xAxis = d3
        .axisBottom(x)
        .ticks(months > 3 ? d3.timeMonday : d3.timeDay)
        .tickFormat(d3.timeFormat(axisDateFormat))

      that.svg
        .append('g')
        .attr('transform', 'translate(0,' + that.height + ')')
        .call(xAxis)
        .selectAll('text')
        .attr('y', 0)
        .attr('x', 9)
        .attr('transform', 'rotate(60)')
        .style('text-anchor', 'start')

      let max = d3.max(stackedData, function(stack) {
        let max = d3.max(stack, function(d) {
          return d[1]
        })
        return max
      })

      // Add Y axis
      var y = d3
        .scaleLinear()
        .domain([0, max])
        .range([that.height, 0])
      that.svg.append('g').call(d3.axisLeft(y))

      // color palette
      var color = d3
        .scaleOrdinal()
        .domain(mygroups)
        .range(that.colors)

      // Show the areas
      that.svg
        .selectAll('mylayers')
        .data(stackedData)
        .enter()
        .append('path')
        .style('fill', function(d) {
          let name = mygroups[d.key - 1]
          return color(name)
        })
        .attr(
          'd',
          d3
            .area()
            .x(function(d) {
              return x(d.data.key)
            })
            .y0(function(d) {
              return y(d[0])
            })
            .y1(function(d) {
              return y(d[1])
            }),
        )

      // Legends
      let keys = mygroups
      let size = 20
      let xOffset = that.width - 100

      that.svg
        .selectAll('myrects')
        .data(keys)
        .enter()
        .append('rect')
        .attr('x', xOffset)
        .attr('y', function(d, i) {
          return 10 + i * (size + 5)
        })
        .attr('width', size)
        .attr('height', size)
        .style('fill', function(d) {
          return color(d)
        })

      that.svg
        .selectAll('mylabels')
        .data(keys)
        .enter()
        .append('text')
        .attr('x', xOffset + size * 1.2)
        .attr('y', function(d, i) {
          return 10 + i * (size + 5) + size / 2
        }) // 100 is where the first dot appears. 25 is the distance between dots
        .style('fill', function(d) {
          return color(d)
        })
        .text(function(d) {
          return d
        })
        .attr('text-anchor', 'left')
        .style('alignment-baseline', 'middle')
    },
  },
}
</script>

<style>
rect.bordered {
  stroke: #e6e6e6;
  stroke-width: 2px;
}

#gradient .tick text {
  fill: #e6e6e6;
}

#gradient .tick line {
  stroke: #e6e6e6;
}

#gradient .domain {
  stroke: #e6e6e6;
}
</style>
